import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { Warning, ProTip } from '../../components/LessonUI';

const HTMLAccessibility = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Semantic HTML</div>
                <h1>Accessibility & ARIA</h1>
                <p className="hero-lead">
                    Build inclusive websites that work for everyone. Master ARIA attributes, roles,
                    keyboard navigation, and accessibility best practices for the modern web.
                </p>
            </div>

            <div className="content-section">
                <h2>1. The Four Pillars of Accessibility: POURL</h2>
                <p>
                    Professional web accessibility is guided by the <strong>Web Content Accessibility Guidelines (WCAG)</strong>,
                    which are built upon four fundamental principles known by the acronym <strong>POURL</strong>.
                    If your website fails any one of these, it is technically considered inaccessible.
                </p>

                <div className="benefits-grid">
                    <div className="benefit-card">
                        <h3>Perceivable</h3>
                        <p>Information and user interface components must be presentable to users in ways they can perceive. This means content cannot be invisible to all of their senses.</p>
                    </div>
                    <div className="benefit-card">
                        <h3>Operable</h3>
                        <p>User interface components and navigation must be operable. The interface cannot require interaction that a user cannot perform (e.g., mouse-only interactions).</p>
                    </div>
                    <div className="benefit-card">
                        <h3>Understandable</h3>
                        <p>Information and the operation of the user interface must be understandable. Users must be able to understand the information as well as the operation of the UI.</p>
                    </div>
                    <div className="benefit-card">
                        <h3>Robust</h3>
                        <p>Content must be robust enough that it can be interpreted reliably by a wide variety of user agents, including assistive technologies like screen readers.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>2. Internal Mechanics: The Accessibility Tree</h2>
                <p>
                    To understand ARIA, you must first understand how a browser works. When a browser loads
                    HTML, it creates a <strong>DOM Tree</strong> (Document Object Model). However, assistive
                    technologies like screen readers do not read the DOM directly. Instead, the browser
                    generates a second, specialized tree called the <strong>Accessibility Tree</strong>.
                </p>
                <ProTip><p><strong>Senior Project Insight:</strong> ARIA is essentially a way to "manually override"
                        or "enrich" the Accessibility Tree. When you use <code>aria-label</code>, you are not
                        changing the pixels on the screen; you are changing the <strong>Accessible Name</strong>
                        property of that node in the Accessibility Tree.</p></ProTip>
                <div className="diagram-box">
                    <CodeBlock language="text" code={`[Source HTML] -&gt; [DOM Tree] -&gt; [Accessibility Tree] -&gt; [Assisitive Technology]
                                          ^
                                          |
                                    [ARIA Attributes]`} />
                </div>
            </div>

            <div className="content-section">
                <h2>3. Why Accessibility Matters</h2>
                <p>
                    Building for accessibility is not just a "nice to have" or a legal checkbox; it's a
                    fundamental requirement for <strong>Universal Design</strong>. Creating a site that
                    works for a blind user also makes the site better for a user with temporary limitations
                    (like a broken arm) or situational limitations (like glare on a screen).
                </p>

                <div className="benefits-grid">
                    <div className="benefit-card">
                        <h3> Visual Disabilities</h3>
                        <p>Blindness, low vision, and color deficiency like protanopia.</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Auditory Disabilities</h3>
                        <p>Deafness or hard of hearing situations (requires captions/transcripts).</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Motor Disabilities</h3>
                        <p>Limitation in fine motor control requiring switch devices or keyboards.</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Cognitive Disabilities</h3>
                        <p>Dyslexia, ADHD, or memory limitations requiring clear, simple navigation.</p>
                    </div>
                </div>

                <ProTip><p><strong>Legal Compliance:</strong> In many jurisdictions, failures in accessibility fall under civil rights legislation (like the ADA in the US or the EAA in Europe). High-traffic commercial sites can face significant litigation for non-compliance.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>4. What is ARIA?</h2>
                <p>
                    <strong>ARIA (Accessible Rich Internet Applications)</strong> is a technical specification
                    developed by the W3C. It provides a set of attributes that allow developers to describe
                    the role, state, and properties of custom UI components.
                </p>

                <Warning><strong> The First Rule of ARIA:</strong> If you can use a native HTML5 element
                    (like <code>&lt;button&gt;</code>, <code>&lt;nav&gt;</code>, or <code>&lt;details&gt;</code>)
                    that already has the behavior and semantics built-in, <strong>do not use ARIA</strong>.
                    No ARIA is better than bad ARIA.</Warning>

                <ProTip><p><strong>Senior Strategy: The ARIA APG.</strong> Professional developers follow the
                        <strong>ARIA Authoring Practices Guide (APG)</strong>. It provides standard keyboard patterns and
                        ARIA attribute requirements for common widgets like Tabs, Accordions, and Modals. Using
                        standardized patterns ensures your custom components feel familiar to screen reader users.</p></ProTip>

                <h3>When to Use ARIA:</h3>
                <ul>
                    <li><strong>Custom Widgets:</strong> Components like Tabs or Tree Views that don't have a native HTML tag.</li>
                    <li><strong>Dynamic Content:</strong> Alerting users to changes in the page (e.g., successful form submission).</li>
                    <li><strong>Contextual Relationships:</strong> Linking a label to a specific input via <code>aria-labelledby</code>.</li>
                    <li><strong>Descriptive Metadata:</strong> Hiding decorative elements from screen readers with <code>aria-hidden</code>.</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>5. The Philosophy of Focus Management</h2>
                <p>
                    For users who navigate via keyboard or switch devices, the <strong>Focus Indicator</strong>
                    (often a blue or orange ring around an element) is their "mouse cursor."
                </p>
                <ProTip><p><strong>Crucial Rule:</strong> Never set <code>outline: none</code> in your CSS
                        without providing a high-contrast alternative focus state. Removing the focus ring
                        makes your site literally impossible to navigate for many users.</p></ProTip>
                <p>
                    Professional focus management also involves <strong>Focus Trapping</strong> inside modals
                    and <strong>Focus Management</strong> when components are removed from the DOM. If a user
                    closes a menu, their focus should return exactly to the button that opened it.
                </p>
            </div>

            <div className="content-section">
                <h2>6. ARIA Attributes Overview</h2>

                <h3>The Three Components: Roles, Properties, and States</h3>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h4>1. Roles (The "Who")</h4>
                        <p>Roles define the element's semantic nature. Once set, a role rarely changes.
                            Example: <code>role="tabpanel"</code> or <code>role="alert"</code>.</p>
                    </div>
                    <div className="concept-card">
                        <h4>2. Properties (The "What")</h4>
                        <p>Properties provide additional metadata that is likely to remain static or change
                            infrequently. Example: <code>aria-labelledby</code> or <code>aria-haspopup</code>.</p>
                    </div>
                    <div className="concept-card">
                        <h4>3. States (The "How")</h4>
                        <p>States are dynamic and change based on user interaction. Example:
                            <code>aria-expanded</code> (true/false) or <code>aria-checked</code>.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>7. Essential ARIA Roles</h2>

                <h3>Landmark Roles: Navigating the Page</h3>
                <p>
                    Screen reader users often navigate by jumping between "Landmarks." While you should
                    prefer semantic tags like <code>&lt;main&gt;</code> and <code>&lt;nav&gt;</code>,
                    using landmark roles ensures legacy compatibility and clarity.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Landmark Roles and HTML5 Equivalents</span>
                    </div>
                    <CodeBlock language="html" code={`<header role="banner"> <!-- Equivalent to <header> at root --&gt;
<nav role="navigation"> <!-- Equivalent to <nav> --&gt;
<main role="main">      <!-- Equivalent to <main> --&gt;
<aside role="complementary"> <!-- Equivalent to <aside> --&gt;
<footer role="contentinfo">  <!-- Equivalent to <footer> at root --&gt;`} />
                </div>

                <h3>Widget Roles: The Architecture of Interaction</h3>
                <p>
                    Widget roles are for components that require JavaScript. If you use a widget role,
                    you are promising the user that your component will behave like a standard desktop
                    application element.
                </p>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- A Tab Interface Blueprint --&gt;
<div role="tablist" aria-label="Project Sections">
    <button role="tab" 
            aria-selected="true" 
            aria-controls="panel-1" 
            id="tab-1">
        Overview
    </button>
    <button role="tab" 
            aria-selected="false" 
            aria-controls="panel-2" 
            id="tab-2" 
            tabindex="-1">
        Specifications
    </button>
</div>

<div id="panel-1" role="tabpanel" aria-labelledby="tab-1">
    <p>Section 1 Content...</p>
</div>
<div id="panel-2" role="tabpanel" aria-labelledby="tab-2" hidden>
    <p>Section 2 Content...</p>
</div>`} />
                </div>
                <ProTip><p><strong>Pro Tip:</strong> In a tab list, only the active tab should have
                        <code>tabindex="0"</code>. Inactive tabs should have <code>tabindex="-1"</code>
                        to allow users to move between them using <strong>Arrow Keys</strong> instead of
                        clogging up the <strong>Tab key</strong> cycle. This is called the
                        <strong>Roving Tabindex</strong> pattern.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>Common ARIA Attributes</h2>

                <h3>1. <code>aria-label</code> - Accessible Name</h3>
                <p>Provides a text label for elements without visible text:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Icon-only button --&gt;
<button aria-label="Close dialog">
    <svg>...</svg>
</button>

<!-- Search form --&gt;
<form role="search">
    <input type="search" aria-label="Search the website">
    <button aria-label="Submit search"></button>
</form>`} />
                </div>

                <h3>2. <code>aria-labelledby</code> - Reference Label</h3>
                <p>Points to element(s) that label this one:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<h2 id="dialog-title">Confirm Delete</h2>
<div role="dialog" aria-labelledby="dialog-title">
    <p>Are you sure you want to delete this item?</p>
    <button>Cancel</button>
    <button>Delete</button>
</div>

<!-- Multiple labels --&gt;
<div id="billing-title">Billing Address</div>
<div id="name-label">Full Name</div>
<input type="text" aria-labelledby="billing-title name-label">`} />
                </div>

                <h3>3. <code>aria-describedby</code> - Additional Description</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<label for="password">Password:</label>
<input type="password" 
       id="password" 
       aria-describedby="password-requirements">
<div id="password-requirements">
    Must be at least 8 characters with uppercase, lowercase, and number
</div>`} />
                </div>

                <h3>4. <code>aria-hidden</code> - Hide from Screen Readers</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Decorative icon --&gt;
<button>
    <svg aria-hidden="true">...</svg>
    Save
</button>

<!-- Font icon --&gt;
<button>
    <i class="fa fa-save" aria-hidden="true"></i>
    Save
</button>`} />
                </div>

                <h3>5. <code>aria-expanded</code> - Collapsible State</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<button aria-expanded="false" aria-controls="menu">
    Menu
</button>
<ul id="menu" hidden>
    <li><a href="/home">Home</a></li>
    <li><a href="/about">About</a></li>
</ul>

<script>
const button = document.querySelector('button');
const menu = document.getElementById('menu');

button.addEventListener('click', () => {
    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', !expanded);
    menu.hidden = expanded;
});
</script>`} />
                </div>

                <div className="content-section">
                    <h2>8. ARIA Live Regions: Handling Dynamic Updates</h2>
                    <p>
                        JavaScript allows us to update the page without a full reload. However, a screen reader user
                        might not notice that a "Success" message has appeared or that their cart total has
                        changed. <strong>ARIA Live Regions</strong> solve this by announcing changes to the user
                        without moving their focus.
                    </p>

                    <div className="code-example">
                        <div className="code-header">
                            <span>Dynamic Progress Indicator</span>
                        </div>
                        <CodeBlock language="html" code={`<div aria-live="polite" 
     aria-atomic="true" 
     aria-busy="true" 
     class="status-container">
    <p>Uploading file... 45% complete</p>
</div>`} />
                    </div>

                    <ProTip><p><strong>The "Atomic" Property:</strong> When <code>aria-atomic="true"</code> is set,
                            the screen reader will read the <strong>entire</strong> region even if only a small
                            part of it changed. If set to false (default), it only reads the specific text that
                            was added or removed.</p></ProTip>

                    <div className="live-regions-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Value</th>
                                    <th>Announcement Logic</th>
                                    <th>Best Use Case</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>polite</code></td>
                                    <td>Waits for the user to stop interacting before speaking.</td>
                                    <td>Updates like "Message Sent" or "3 items in cart."</td>
                                </tr>
                                <tr>
                                    <td><code>assertive</code></td>
                                    <td>Interrupts the current speech immediately.</td>
                                    <td>Error messages or urgent system alerts.</td>
                                </tr>
                                <tr>
                                    <td><code>off</code></td>
                                    <td>Suppress all announcements.</td>
                                    <td>Content that changes frequently but isn't critical (e.g., stock tickers).</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="content-section">
                    <h2>9. Forms and Labels: The Myth of the Placeholder</h2>
                    <p>
                        A common mistake in modern design is using the <code>placeholder</code> attribute as a
                        replacement for a <code>&lt;label&gt;</code> element to "save space."
                    </p>

                    <ProTip><p><strong>Why Placeholders Fail Accessibility:</strong></p>
                        <ol>
                            <li><strong>Low Contrast:</strong> Browsers default to a light gray that often fails
                                WCAG contrast ratios.</li>
                            <li><strong>Disappearing Act:</strong> Once the user starts typing, the label
                                disappears. Users with cognitive disabilities may forget what they were supposed
                                to type.</li>
                            <li><strong>Screen Reader Inconsistency:</strong> Not all screen readers announce
                                placeholders when moving through a form.</li>
                        </ol></ProTip>

                    <div className="code-example">
                        <div className="code-header">
                            <span>Accessible Form Pattern</span>
                        </div>
                        <CodeBlock language="html" code={`<div class="form-group">
    <label for="user-email">Email Address (Required)</label>
    <input type="email" 
           id="user-email" 
           name="email" 
           required 
           aria-required="true"
           placeholder="e.g., alex@example.com">
</div>`} />
                    </div>
                </div>

                <h3>6. <code>aria-live</code> - Dynamic Content Updates</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Polite: wait for screen reader to finish --&gt;
<div aria-live="polite" aria-atomic="true">
    <p{"&gt;"} 3 items in cart</p>
</div>

<!-- Assertive: interrupt immediately --&gt;
<div aria-live="assertive" role="alert">
    <p>Error: Payment failed</p>
</div>

<!-- Off: no announcement (default) --&gt;
<div aria-live="off">
    Regular content
</div>`} />
                </div>

                <div className="live-regions-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Value</th>
                                <th>When to Use</th>
                                <th>Example</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>off</code></td>
                                <td>Default, no announcement</td>
                                <td>Static content</td>
                            </tr>
                            <tr>
                                <td><code>polite</code></td>
                                <td>Announce when convenient</td>
                                <td>Status updates, cart count</td>
                            </tr>
                            <tr>
                                <td><code>assertive</code></td>
                                <td>Interrupt immediately</td>
                                <td>Errors, urgent alerts</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Keyboard Navigation</h2>
                <p>
                    All interactive elements must be keyboard-accessible:
                </p>

                <h3>Native Keyboard Support</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- These are keyboard-accessible by default: --&gt;
<a href="/page">Link</a>                    <!-- Tab, Enter --&gt;
<button>Button</button>                     <!-- Tab, Enter/Space --&gt;
<input type="text">                         <!-- Tab, typing --&gt;
<textarea></textarea>                       <!-- Tab, typing --&gt;
<select><option>...</option></select>       <!-- Tab, Arrow keys --&gt;`} />
                </div>

                <h3>Making Custom Elements Keyboard-Accessible</h3>
                <div className="code-example">
                    <div className="code-header">
                        <span>Custom Button with Keyboard Support</span>
                    </div>
                    <CodeBlock language="html" code={`<div role="button" 
     tabindex="0" 
     onclick="handleClick(event)"
     onkeydown="handleKeyPress(event)">
    Custom Button
</div>

<script>
function handleClick(e) {
    console.log('Clicked!');
}

function handleKeyPress(e) {
    // Handle Enter and Space keys
    if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick(e);
    }
}
</script>

<style>
[role="button"]:focus {
    outline: 2px solid #007bff;
    outline-offset: 2px;
}
</style>`} />
                </div>

                <h3>Tabindex Attribute</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- tabindex="0": Include in natural tab order --&gt;
<div tabindex="0" role="button">Focusable</div>

<!-- tabindex="-1": Focusable by JavaScript, not Tab key --&gt;
<div tabindex="-1" id="skip-target">Skip link target</div>

<!-- tabindex="1+": AVOID! Disrupts natural tab order --&gt;
<input tabindex="1">  <!-- DON'T DO THIS --&gt;`} />
                </div>

                <Warning><strong> Never use positive tabindex values!</strong> They disrupt the natural
                    tab order and confuse users.</Warning>

                <h3>Focus Management</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Modal dialog focus management --&gt;
<button onclick="openDialog()">Open Dialog</button>

<div role="dialog" 
     aria-modal="true" 
     aria-labelledby="dialog-title" 
     hidden>
    <h2 id="dialog-title">Dialog Title</h2>
    <p>Dialog content</p>
    <button onclick="closeDialog()">Close</button>
</div>

<script>
let previousFocus;

function openDialog() {
    const dialog = document.querySelector('[role="dialog"]');
    const closeButton = dialog.querySelector('button');
    
    // Save current focus
    previousFocus = document.activeElement;
    
    // Show dialog
    dialog.hidden = false;
    
    // Move focus to dialog
    closeButton.focus();
    
    // Trap focus inside dialog
    dialog.addEventListener('keydown', trapFocus);
}

function closeDialog() {
    const dialog = document.querySelector('[role="dialog"]');
    
    // Hide dialog
    dialog.hidden = true;
    
    // Restore focus
    previousFocus.focus();
}

function trapFocus(e) {
    const dialog = e.currentTarget;
    const focusableElements = dialog.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
        }
    }
    
    if (e.key === 'Escape') {
        closeDialog();
    }
}
</script>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Skip Links</h2>
                <p>
                    Allow keyboard users to skip repetitive content:
                </p>

                <div className="code-example">
                    <CodeBlock language="html" code={`<body>
    <a href="#main-content" class="skip-link">Skip to main content</a>
    
    <header>
        <nav>
            <!-- Long navigation menu --&gt;
        </nav>
    </header>
    
    <main id="main-content" tabindex="-1">
        <!-- Main content --&gt;
    </main>
</body>

<style>
.skip-link {
    position: absolute;
    top: -40px;
    left: 0;
    background: #000;
    color: #fff;
    padding: 8px 16px;
    text-decoration: none;
    z-index: 100;
}

.skip-link:focus {
    top: 0;
}
</style>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Accessible Component Examples</h2>

                <h3>Accordion</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<div class="accordion">
    <h3>
        <button aria-expanded="false" 
                aria-controls="panel1" 
                id="accordion1">
            Section 1
        </button>
    </h3>
    <div id="panel1" 
         role="region" 
         aria-labelledby="accordion1" 
         hidden>
        <p>Panel content...</p>
    </div>
    
    <!-- More sections... --&gt;
</div>

<script>
document.querySelectorAll('.accordion button').forEach(button => {
    button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        
        button.setAttribute('aria-expanded', !expanded);
        panel.hidden = expanded;
    });
});
</script>`} />
                </div>

                <h3>Tooltip</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<button aria-describedby="tooltip">
    Help
</button>
<div id="tooltip" role="tooltip" hidden>
    This is helpful information
</div>

<script>
const button = document.querySelector('button');
const tooltip = document.getElementById('tooltip');

button.addEventListener('mouseenter', () => tooltip.hidden = false);
button.addEventListener('mouseleave', () => tooltip.hidden = true);
button.addEventListener('focus', () => tooltip.hidden = false);
button.addEventListener('blur', () => tooltip.hidden = true);
</script>`} />
                </div>

                <h3>Alert/Notification</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<div role="alert" aria-live="assertive" class="alert" hidden>
    <p>Your changes have been saved!</p>
    <button aria-label="Close notification"></button>
</div>

<script>
function showAlert(message) {
    const alert = document.querySelector('[role="alert"]');
    alert.querySelector('p').textContent = message;
    alert.hidden = false;
    
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
        alert.hidden = true;
    }, 5000);
}
</script>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Images & Alt Text</h2>

                <div className="alt-text-guide">
                    <h3>Writing Good Alt Text:</h3>

                    <h4>1. Informative Images</h4>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<!-- Describe the content/function --&gt;
<img src="chart.png" alt="Sales increased 45% from Q3 to Q4 2024">

<!-- Not: alt="chart" or alt="image of chart" --&gt;`} />
                    </div>

                    <h4>2. Decorative Images</h4>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<!-- Empty alt for decorative images --&gt;
<img src="decorative-border.png" alt="">

<!-- Or use CSS background images --&gt;`} />
                    </div>

                    <h4>3. Linked Images</h4>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<!-- Describe the link destination --&gt;
<a href="/home">
    <img src="logo.png" alt="Home page">
</a>

<!-- Not: alt="Company logo" --&gt;`} />
                    </div>

                    <h4>4. Complex Images</h4>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<!-- Provide detailed description --&gt;
<figure>
    <img src="complex-chart.png" 
         alt="Bar chart showing monthly sales"
         aria-describedby="chart-details">
    <figcaption id="chart-details">
        Detailed description: January $10k, February $12k, 
        March $15k, showing steady growth...
    </figcaption>
</figure>`} />
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Color & Contrast</h2>

                <h3>WCAG Contrast Requirements:</h3>
                <div className="contrast-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Level</th>
                                <th>Normal Text</th>
                                <th>Large Text (18pt+)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>AA (Minimum)</td>
                                <td>4.5:1</td>
                                <td>3:1</td>
                            </tr>
                            <tr>
                                <td>AAA (Enhanced)</td>
                                <td>7:1</td>
                                <td>4.5:1</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <Warning><strong> Never use color alone</strong> to convey information. Always provide a
                    text alternative or icon.</Warning>

                <div className="code-example">
                    <div className="code-header">
                        <span> Bad: Color Only</span>
                    </div>
                    <CodeBlock language="html" code={`<span style="color: red;">Required</span>
<span style="color: green;">Available</span>`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Good: Color + Text/Icon</span>
                    </div>
                    <CodeBlock language="html" code={`<span style="color: red;">* Required</span>
<span style="color: green;"> Available</span>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>12. Testing with Assistive Technology</h2>
                <p>
                    Automated tools (like Lighthouse or Axe) can only catch about 30-40% of accessibility
                    issues. The remaining 60-70% require manual testing with a screen reader.
                </p>

                <h3>Essential Screen Reader Shortcuts for Developers:</h3>
                <div className="shortcuts-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Action</th>
                                <th>NVDA (Windows)</th>
                                <th>VoiceOver (Mac)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Next Heading</strong></td>
                                <td><kbd>H</kbd></td>
                                <td><kbd>VO + Cmd + H</kbd></td>
                            </tr>
                            <tr>
                                <td><strong>Next Button</strong></td>
                                <td><kbd>B</kbd></td>
                                <td><kbd>VO + Cmd + J</kbd></td>
                            </tr>
                            <tr>
                                <td><strong>List Landmarks</strong></td>
                                <td><kbd>Insert + F7</kbd></td>
                                <td><kbd>VO + U</kbd> (Rotor)</td>
                            </tr>
                            <tr>
                                <td><strong>Read Current Line</strong></td>
                                <td><kbd>Insert + Up Arrow</kbd></td>
                                <td><kbd>VO + L</kbd></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <ProTip><p><strong>Professional Workflow:</strong> When testing, close your eyes or turn off
                        your monitor. Attempt to navigate the page using only the keyboard and the screen
                        reader's audio feedback. If you get "stuck" or cannot find a piece of information,
                        your accessibility hierarchy is broken.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>13. Cognitive Accessibility: Beyond the Physical</h2>
                <p>
                    Accessibility is often misunderstood as being only for visual or motor physical disabilities.
                    <strong>Cognitive Accessibility</strong> is equally important and focuses on users with
                    dyslexia, ADHD, memory issues, or learning disabilities.
                </p>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h4>Predictability</h4>
                        <p>Navigation and layout should be consistent across every page of your site.
                            Don't move the search bar or the login button.</p>
                    </div>
                    <div className="concept-card">
                        <h4>Error Prevention</h4>
                        <p>Forms should be forgiving. provide clear error messages and suggestions
                            on how to fix the input rather than just saying "Invalid."</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>14. The "Div-itis" Impact: Why Semantic Choice Matters</h2>
                <p>
                    A common anti-pattern in modern development is using a <code>&lt;div&gt;</code> for
                    everything and trying to "fix" it with ARIA.
                </p>
                <Warning><p><strong>The Hidden Cost of Div-itis:</strong> Even with <code>role="button"</code>,
                        a div does not automatically gain keyboard support. You have to manually write JavaScript
                        for the <strong>Space</strong> and <strong>Enter</strong> keys, manage focus states,
                        and handle hover effects. By using a native <code>&lt;button&gt;</code>, you inherit
                        all of this functionality for free, reducing your bundle size and preventing bugs.</p></Warning>
            </div>

            <div className="content-section">
                <h2>15. The Accessibility First Mindset: A Professional Summary</h2>
                <p>
                    Universal Design is not just about disabled users; it's about building <strong>Robust</strong>
                    software that behaves predictably for everyone. An accessible site is:
                </p>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h4>SEO Optimized</h4>
                        <p>Search engines use the same semantic hierarchy as screen readers. Valid accessibility
                            directly translates to better search rankings.</p>
                    </div>
                    <div className="concept-card">
                        <h4>Platform Agnostic</h4>
                        <p>Whether a user is on a mobile phone, a braille display, or a standard laptop,
                            well-structured HTML ensures your content remains readable.</p>
                    </div>
                    <div className="concept-card">
                        <h4>Future-Proof</h4>
                        <p>Standardized ARIA patterns ensure compatibility with future assistive technologies
                            that haven't even been invented yet.</p>
                    </div>
                </div>
                <p className="mt-4">
                    As you build, don't treat accessibility as a final "audit" phase. Treat it as
                    the <strong>foundational architecture</strong> of your code. Think of it this way:
                    If you can navigate your complex application with your eyes closed, you have achieved
                    true engineering excellence.
                </p>
            </div>

        </div>
    );
};

export default HTMLAccessibility;




